import { useCallback, useEffect, useRef, useState } from 'react';
import { StaysApiError, listExpectedStays } from '../../api/stays-client';
import { ExpectedStayRow, ExpectedStays, ExpectedStaysCounts } from '../../interfaces/stays';
import { usePortalLiveRefresh } from './use-portal-live-refresh';
import { EXPECTED_POLL_MS } from './use-expected-today';

/**
 * TODAY'S SHORT-STAY GUESTS FOR ONE BUILDING — GET /stays/desk/buildings/:b/expected
 * (Lobbero Stays plan S3, the desk lane).
 *
 * The same two readers as useExpectedToday — the main menu's Expected card (its
 * badge, and whether it shows at all) and the Expected today screen — and the
 * SAME refresh rules, copied rather than shared so the two lists can never hold
 * each other back:
 *   - on window focus and when the tab becomes visible again;
 *   - every 60 s while the page is visible;
 *   - when the bell's socket delivers a `visitor.arrived` frame or any
 *     `stays.` frame (the one socket the app holds — never a second);
 *   - whenever the caller asks (after every sign-in and sign-out).
 *
 * ONE READ WINS, AND IT IS THE LATEST ONE: the read-sequence guard, so an older
 * list overtaking a newer one never puts a guest just signed in back on the
 * list as expected. A changed building throws the old answer away at once.
 *
 * WHO DECIDES WHETHER THE LANE EXISTS. A concierge holds stays:desk but not
 * stays:read, so the organisation's entitlement read answers them 403 and is
 * no use here. This read's own `stays_enabled` is the live word for THIS
 * building; null until the first read answers.
 */

/** The bell frames that mean this lane may have changed. Prefixes. */
export const EXPECTED_STAYS_LIVE_EVENTS = ['visitor.arrived', 'stays.'];

export type ExpectedStaysLoadError = 'denied' | 'failed';

export interface ExpectedStaysState {
  /** null until the first read for this building answers. */
  rows: ExpectedStayRow[] | null;
  /** The building's live Stays switch, null until known. false hides the lane. */
  staysEnabled: boolean | null;
  counts: ExpectedStaysCounts | null;
  deskDay: ExpectedStays['desk_day'] | null;
  /** The server's clock at the moment it answered. */
  generatedAt: string | null;
  loading: boolean;
  refreshing: boolean;
  /** 'denied' = 403; 'failed' = anything else. Cleared by the next good read. */
  error: ExpectedStaysLoadError | null;
  refresh: () => Promise<void>;
}

export interface UseExpectedStaysOptions {
  /** Off = no reads at all (no stays:desk, or no building). */
  enabled?: boolean;
}

export const useExpectedStays = (
  buildingId: number | null | undefined,
  { enabled = true }: UseExpectedStaysOptions = {}
): ExpectedStaysState => {
  const id =
    typeof buildingId === 'number' && Number.isInteger(buildingId) && buildingId > 0
      ? buildingId
      : null;
  const active = enabled && id !== null;

  const [data, setData] = useState<ExpectedStays | null>(null);
  const [inFlight, setInFlight] = useState(false);
  const [error, setError] = useState<ExpectedStaysLoadError | null>(null);
  const readSeq = useRef(0);

  const refresh = useCallback(async (): Promise<void> => {
    if (!active || id === null) return;
    const seq = ++readSeq.current;
    setInFlight(true);
    try {
      const res = await listExpectedStays(id);
      if (seq !== readSeq.current) return;
      setData(res);
      setError(null);
    } catch (e) {
      if (seq !== readSeq.current) return;
      const status = e instanceof StaysApiError ? e.status : null;
      setError(status === 403 ? 'denied' : 'failed');
    } finally {
      if (seq === readSeq.current) setInFlight(false);
    }
  }, [active, id]);

  // A new building (or the gate opening or closing) starts from nothing. The
  // bump invalidates any read still in flight for the previous building.
  useEffect(() => {
    readSeq.current += 1;
    setData(null);
    setError(null);
    setInFlight(false);
    if (active) void refresh();
  }, [active, id, refresh]);

  useEffect(() => {
    if (!active) return undefined;

    const onFocus = () => {
      void refresh();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') void refresh();
    }, EXPECTED_POLL_MS);

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [active, refresh]);

  usePortalLiveRefresh(EXPECTED_STAYS_LIVE_EVENTS, () => {
    if (active) void refresh();
  });

  return {
    rows: data ? data.rows || [] : null,
    staysEnabled: data ? data.stays_enabled === true : null,
    counts: data ? data.counts : null,
    deskDay: data?.desk_day ?? null,
    generatedAt: data?.generated_at ?? null,
    loading: active && data === null && inFlight,
    refreshing: data !== null && inFlight,
    error,
    refresh,
  };
};
